import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { MarketplaceInfo } from 'src/api/v1-api';
import Link from 'next/link';

const PAGES_OPTIONS = [
  { name: 'Home', value: 'home' },
  { name: 'Category', value: 'category' },
  { name: 'Listing', value: 'listing' },
];

const COLLECT_PAGES_OPTIONS = [
  { name: 'Home', value: 'home' },
  { name: 'Category', value: 'category' },
  { name: 'Listing', value: 'listing' },
  { name: 'Link In Bio', value: 'linkinbio' },
];

export interface FeaturesFormFields {
  collectEmailModule: CollectEmailModuleConfig;
  displayInactiveListingInfo: boolean;
  showShoppingCart: boolean;
  marketplaceBanner: MarketplaceBannerConfig;
  showFirstPartyListingsFirst: boolean;
  enableBuyerDigestEmails: boolean;
}

export interface CollectEmailModuleConfig {
  title: string;
  description: string;
  cta_text: string;
  pages: string[];
  position: string;
}

export interface MarketplaceBannerConfig {
  title: string;
  description: string;
  cta_text: string;
  cta_link: string;
  pages: string[];
}

export interface FeaturesFormProps {
  marketplace: MarketplaceInfo | null;
  isLoading?: boolean;
  onSubmit: (data: FeaturesFormFields) => void;
}

export const FeaturesForm = ({ marketplace, isLoading, onSubmit }: FeaturesFormProps) => {
  const [displayInactiveListingInfo, setDisplayInactiveListingInfo] = useState(true);
  const [showFirstPartyListingsFirst, setShowFirstPartyListingsFirst] = useState(true);
  const [showShoppingCart, setShowShoppingCart] = useState(true);
  const [enableBuyerDigestEmails, setEnableBuyerDigestEmails] = useState(true);

  const [collectEmailModule, setCollectEmailModule] = useState<CollectEmailModuleConfig>({
    title: marketplace?.functional?.collect_email_module?.title
      ? marketplace?.functional?.collect_email_module?.title
      : 'Get the latest updates',
    description: marketplace?.functional?.collect_email_module?.description
      ? marketplace?.functional?.collect_email_module?.description
      : 'Sign up to receive a weekly email',
    cta_text: marketplace?.functional?.collect_email_module?.cta_text
      ? marketplace?.functional?.collect_email_module?.cta_text
      : 'Signup',
    pages: marketplace?.functional?.collect_email_module?.pages
      ? marketplace?.functional?.collect_email_module?.pages
      : [],
    position: marketplace?.functional?.collect_email_module?.position
      ? marketplace?.functional?.collect_email_module?.position
      : 'bottom',
  });

  const [marketplaceBanner, setMarketplaceBanner] = useState<MarketplaceBannerConfig>({
    title: marketplace?.functional?.add_listing_banner?.title
      ? marketplace?.functional?.add_listing_banner?.title
      : '',
    description: marketplace?.functional?.add_listing_banner?.description
      ? marketplace?.functional?.add_listing_banner?.description
      : '',
    cta_text: marketplace?.functional?.add_listing_banner?.cta_text
      ? marketplace?.functional?.add_listing_banner?.cta_text
      : '',
    cta_link: marketplace?.functional?.add_listing_banner?.cta_link
      ? marketplace?.functional?.add_listing_banner?.cta_link
      : '',
    pages: marketplace?.functional?.add_listing_banner?.pages
      ? marketplace?.functional?.add_listing_banner?.pages
      : [],
  });

  useEffect(() => {
    if (marketplace) {
      setDisplayInactiveListingInfo(marketplace.functional?.show_inactive_listings_info);
      setShowFirstPartyListingsFirst(
        marketplace.functional?.show_first_party_listings_first ?? false,
      );
      setEnableBuyerDigestEmails(marketplace.functional?.enable_buyer_digest_emails ?? false);
      setShowShoppingCart(marketplace.functional?.show_shopping_cart ?? true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace]);

  if (!marketplace) {
    return <div>Loading...</div>;
  }

  const handleCollectEmailModuleChange = (
    key: Exclude<keyof CollectEmailModuleConfig, 'pages'>,
    value: string,
  ) => {
    setCollectEmailModule({ ...collectEmailModule, [key]: value });
  };

  const handleCollectPagesChanges = (value: (number | string)[]) => {
    setCollectEmailModule({ ...collectEmailModule, pages: value as string[] });
  };

  const handleBannerChange = (
    key: Exclude<keyof MarketplaceBannerConfig, 'pages'>,
    value: string,
  ) => {
    setMarketplaceBanner({ ...marketplaceBanner, [key]: value });
  };

  const handlePagesChanges = (value: (number | string)[]) => {
    setMarketplaceBanner({ ...marketplaceBanner, pages: value as string[] });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      collectEmailModule,
      displayInactiveListingInfo,
      showShoppingCart,
      marketplaceBanner,
      showFirstPartyListingsFirst,
      enableBuyerDigestEmails,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Stack justify="flex-start" align="flex-start" spacing="20px" w="100%">
        <FormControl display="flex" alignItems="center" py={2}>
          <Box flex="1">
            <FormLabel fontWeight="medium" mb="4px">
              Redirect to Home Screen on non-Active Listing Page
            </FormLabel>
            <Text fontSize="sm" color="gray.600">
              Automatically redirect visitors to home page when viewing inactive listings instead of
              showing a 404 page
            </Text>
          </Box>
          <Switch
            size="lg"
            isChecked={displayInactiveListingInfo}
            onChange={(event) => setDisplayInactiveListingInfo(event.target.checked)}
          />
        </FormControl>

        <Divider />

        <FormControl display="flex" alignItems="center" py={2}>
          <Box flex="1">
            <FormLabel fontWeight="medium" mb="4px">
              Display Marketplace Listings First
            </FormLabel>
            <Text fontSize="sm" color="gray.600">
              Order listings created on your marketplace before imported third-party listings
            </Text>
          </Box>
          <Switch
            size="lg"
            isChecked={showFirstPartyListingsFirst}
            onChange={(event) => setShowFirstPartyListingsFirst(event.target.checked)}
          />
        </FormControl>

        <Divider />

        <FormControl display="flex" alignItems="center" py={2}>
          <Box flex="1">
            <FormLabel fontWeight="medium" mb="4px">
              Show Shopping Cart Link in Nav Bar
            </FormLabel>
            <Text fontSize="sm" color="gray.600">
              Display shopping cart icon and link in the navigation bar
            </Text>
          </Box>
          <Switch
            size="lg"
            isChecked={showShoppingCart}
            onChange={(event) => setShowShoppingCart(event.target.checked)}
          />
        </FormControl>

        <Divider />

        <Stack justify="flex-start" align="flex-start" spacing="20px" w="100%">
          <Text fontFamily="Inter" lineHeight="1.5" fontWeight="semibold" fontSize="16px">
            Display Collect Visitor Emails Form
          </Text>
          <Text fontSize="sm" color="gray.600" mb="20px">
            Add a form to marketplace pages to collect emails for new listing digests
          </Text>
          <FormControl>
            <FormLabel>Title</FormLabel>
            <Input
              placeholder="Title"
              value={collectEmailModule.title}
              onChange={(e) => handleCollectEmailModuleChange('title', e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              placeholder="Description"
              value={collectEmailModule.description}
              onChange={(e) => handleCollectEmailModuleChange('description', e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Signup Button Text</FormLabel>
            <Input
              placeholder="Signup Button Text"
              value={collectEmailModule.cta_text}
              onChange={(e) => handleCollectEmailModuleChange('cta_text', e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Pages</FormLabel>
            <CheckboxGroup
              onChange={handleCollectPagesChanges}
              value={collectEmailModule.pages as (number | string)[]}
            >
              <Stack direction="column">
                {COLLECT_PAGES_OPTIONS.map((page) => (
                  <Checkbox colorScheme="primary" key={page.name} value={page.value}>
                    {page.name}
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          </FormControl>
          <FormControl width="unset">
            <FormLabel>Position</FormLabel>
            <Select
              value={collectEmailModule.position}
              onChange={(e) => handleCollectEmailModuleChange('position', e.target.value)}
            >
              <option value="bottom">Bottom of Pages</option>
              <option value="top">Top of Pages</option>
            </Select>
          </FormControl>
        </Stack>

        <FormControl display="flex" alignItems="center" py={2}>
          <Box flex="1">
            <FormLabel fontWeight="medium" mb="4px">
              Enable Buyer Digest Emails
            </FormLabel>
            <Text fontSize="sm" color="gray.600">
              Send weekly digest emails to buyers with new listings and updates
            </Text>
          </Box>
          <Switch
            size="lg"
            isChecked={enableBuyerDigestEmails}
            onChange={(event) => setEnableBuyerDigestEmails(event.target.checked)}
          />
        </FormControl>

        <Divider />

        <Stack justify="flex-start" align="flex-start" spacing="20px" w="100%">
          <Text lineHeight="1.5" fontWeight="semibold" fontSize="16px">
            Display Add Listing Banner
          </Text>
          <Text fontSize="sm" color="gray.600" mb="20px">
            Show a banner to your visitors to encourage them to create a listing
          </Text>
          <Stack direction="row" justify="flex-start" align="flex-start" spacing="20px">
            <FormControl>
              <FormLabel>Title</FormLabel>
              <Input
                placeholder="Title"
                value={marketplaceBanner.title}
                onChange={(e) => handleBannerChange('title', e.target.value)}
              />
            </FormControl>
          </Stack>
          <Stack direction="row" justify="flex-start" align="flex-start" spacing="20px">
            <FormControl>
              <FormLabel>Button Text</FormLabel>
              <Input
                placeholder="e.g. Add Listing"
                value={marketplaceBanner.cta_text}
                onChange={(e) => handleBannerChange('cta_text', e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Button Link</FormLabel>
              <Input
                placeholder="e.g. /listing/create"
                value={marketplaceBanner.cta_link}
                onChange={(e) => handleBannerChange('cta_link', e.target.value)}
              />
            </FormControl>
          </Stack>
          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              placeholder="e.g. Get seen by the best audience"
              value={marketplaceBanner.description}
              onChange={(e) => handleBannerChange('description', e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Pages</FormLabel>
            <CheckboxGroup
              onChange={handlePagesChanges}
              value={marketplaceBanner.pages as (number | string)[]}
            >
              <Stack direction="column">
                {PAGES_OPTIONS.map((page) => (
                  <Checkbox colorScheme="primary" key={page.name} value={page.value}>
                    {page.name}
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          </FormControl>
        </Stack>
      </Stack>
      <Flex direction={'row'} justifyContent={'flex-end'} alignItems={'center'} pt={10}>
        <Box display="flex" justifyContent="flex-end">
          <Link href="/">
            <Button variant="outline" colorScheme="primary" mr={4}>
              Cancel
            </Button>
          </Link>
          <Button isLoading={isLoading} colorScheme="primary" type="submit" data-test="submit-btn">
            Save
          </Button>
        </Box>
      </Flex>
    </form>
  );
};

export default FeaturesForm;
